import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { gql } from '@apollo/client/core';
import { OuiAutocompleteOption } from 'omnium-ui/form-field';
import { QueryManagerService } from 'projects/box-lib/src/lib/services/queryManagerService';
import { debounceTime, distinctUntilChanged, firstValueFrom } from 'rxjs';
import {
  NatureOperation,
  NatureOperationFilterInput,
  Operation,
  OperationConfig,
  OperationConfigFilterInput,
} from '../../models/generated/graphql';

const operationConfigQuery = gql`
  query operationConfig($filter: OperationConfigFilterInput) {
    operationConfig(where: $filter) {
      id
      natureOperationId
      natureOperation {
        id
        libelle
      }
      enveloppeId
      allowMultiple
    }
  }
`;

@Component({
    selector: 'app-search-nature-autocomplete',
    templateUrl: './search-nature-autocomplete.component.html',
    styleUrls: ['./search-nature-autocomplete.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SearchNatureAutocompleteComponent {
  @Input() currentNatureId: number | undefined;
  @Input() isRequired: boolean = false;
  @Input() isDisabled: boolean = true;
  @Input() enveloppeId: number | undefined;

  @Output() onNatureSelected = new EventEmitter<number | null>();

  natures: NatureOperation[];
  operationConfigs: OperationConfig[];

  private EXCLUDED_NATURE_IDS = [1]; // IDs to exclude (business rule)

  operationConfigFilter: OperationConfigFilterInput;

  natureAutocompleteOptions: OuiAutocompleteOption[] = [];
  natureControl: FormControl;
  initNatureLibelle: string | undefined;

  isLoading: boolean = false;
  constructor(private queryManager: QueryManagerService) {
    this.operationConfigFilter = { and: [] };
  }

  ngOnInit() {
    this.isLoading = true;
    // init the control
    this.natureControl = new FormControl<number | undefined>(this.currentNatureId);
    // build filter

    this.operationConfigFilter.and = [];
    this.operationConfigFilter.and.push({ natureOperationId: { nin: this.EXCLUDED_NATURE_IDS } });
    if (this.enveloppeId) {
      this.operationConfigFilter.and.push({ enveloppeId: { eq: this.enveloppeId } });
    }

    this.fetchNaturesFromOperationConfig(this.operationConfigFilter).then(() => {
      this.natureControl.setValue(this.currentNatureId);
      this.natureControl.updateValueAndValidity();
      if (this.isRequired) {
        this.natureControl.setValidators(Validators.required);
      }
      this.isLoading = false;
      this.natureControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(newNatureId => {
        if (newNatureId && Number.isInteger(newNatureId)) {
          if (this.natures) {
            const newNature = this.natures.find(nature => nature.id === newNatureId);
            if (newNature) {
              this.onNatureSelected.emit(newNatureId);
            }
          }
        } else {
          this.onNatureSelected.emit(null);
          if (this.natures && newNatureId != null) {
            const possibleNatures = this.natures.filter(nature =>
              nature.libelle.toUpperCase().includes(newNatureId.toString().toUpperCase())
            );
            this.setNaturesOptionsFromOperationConfig(this.operationConfigs);
          }
        }
      });
    });
  }

  async fetchNaturesFromOperationConfig(filter: OperationConfigFilterInput) {
    let result = await firstValueFrom(
      this.queryManager.query<{ operationConfig: OperationConfig[] }>({
        query: operationConfigQuery,
        variables: {
          filter: filter,
        },
      })
    );
    this.operationConfigs = result.data.operationConfig;
    this.natures = this.operationConfigs.map(opConfig => opConfig.natureOperation);
    this.setNaturesOptionsFromOperationConfig(this.operationConfigs);
  }

  setNaturesOptionsFromOperationConfig(operationConfigs: OperationConfig[]) {
    this.natureAutocompleteOptions = operationConfigs
      .map(nature => {
        return { label: nature.natureOperation.libelle, value: nature.natureOperation.id };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }
  markAsTouched(): void {
    this.natureControl.markAsTouched();
    this.natureControl.updateValueAndValidity();
  }
}
